import { Box, EmptyState, EmptyStateList, Loader } from "@eworkit/iris";
import React, { DependencyList, FC, ReactNode, useEffect, useRef, useState } from "react";

import { IJobRequest } from "@/data/job-requests";
import { Flex } from "@/eworkitComponents";
import { useInfiniteScroll } from "@/hooks/InfinityScroll";

export interface IInfinityListProps {
  renderRecord: (record: any, handleUpdate: (record: any) => void) => ReactNode;
  fetchParams?: any;
  fetchFunction: (params: any) => Promise<any>;
  renderNoMoreItems?: () => ReactNode;
  deps?: DependencyList;
  scrollContainer?: any;
  noResultsHeader: string;
  noResultsText?: string;
  emptyState?: ReactNode;
  handleItemsCountChange?: (count: number) => void;
  hideInitialLoader?: boolean;
}

export const InfinityList: FC<IInfinityListProps> = ({
  renderRecord,
  fetchParams,
  fetchFunction,
  renderNoMoreItems,
  deps = [],
  scrollContainer,
  noResultsHeader,
  noResultsText,
  emptyState,
  handleItemsCountChange,
  hideInitialLoader,
}) => {
  const [items, setItems] = useState<IJobRequest[]>([]);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [currentParams, setCurrentParams] = useState(fetchParams);
  const [didMount, setDidMount] = useState(false);
  const [loading, setLoading] = useState(true);

  const latestFetchPromise = useRef<Promise<unknown>>();

  useEffect(() => {
    handleItemsCountChange && handleItemsCountChange(items.length);
  }, [handleItemsCountChange, items.length]);

  useEffect(() => {
    setDidMount(true);
  }, []);

  useEffect(() => {
    setCurrentParams(fetchParams);
    setCurrentPage(0);
  }, deps);

  useEffect(() => {
    if (didMount) {
      setLoading(true);

      const newFetchPromise = fetchFunction({ page: currentPage, ...currentParams }).then((response: any) => {
        if (latestFetchPromise.current === newFetchPromise) {
          setLoading(false);
          setItems(items => (currentPage === 0 ? response.content : [...items, ...response.content]));
          setHasNextPage(!response.last);
        }
      });

      latestFetchPromise.current = newFetchPromise;
    }
  }, [currentPage, currentParams, didMount, fetchFunction]);

  const handleUpdate = (record: any) => {
    setItems(items.map(item => (item.id === record.id ? record : item)));
  };

  function handleLoadMore() {
    setLoading(true);
    setCurrentPage(currentPage + 1);
  }

  const infiniteRef = useInfiniteScroll({
    loading,
    hasNextPage,
    onLoadMore: handleLoadMore,
    scrollContainer,
  });

  if (loading && currentPage == 0) {
    if (hideInitialLoader) {
      return null;
    }
    return (
      <Flex align="center" justify="center" padding={["md", false, false, false]}>
        <Loader size="xl" color="vg500" />
      </Flex>
    );
  }

  if (!items.length && !loading) {
    return emptyState ? (
      <>{emptyState}</>
    ) : (
      <Box py="xxl" px="sm">
        <EmptyState icon={<EmptyStateList />} title={noResultsHeader} content={noResultsText} />
      </Box>
    );
  }

  return (
    <div ref={infiniteRef}>
      <Flex innerMargin="md" direction="column">
        <Flex direction="column">{items.map(record => renderRecord(record, handleUpdate))}</Flex>
        {loading && (
          <Flex align="center" justify="center">
            <Loader size="xl" color="vg500" />
          </Flex>
        )}
        {!hasNextPage && renderNoMoreItems && renderNoMoreItems()}
      </Flex>
    </div>
  );
};
